import { GHALISHOR_API_V1_PREFIX } from './identity-access';
import type { IsoDateTimeString, UuidString } from './identity-access';

export const FLEET_ROUTES = Object.freeze({
  updateDriverLocation: `${GHALISHOR_API_V1_PREFIX}/business/driver/location`,
  businessDrivers: `${GHALISHOR_API_V1_PREFIX}/business/dispatch/drivers`,
  dispatchCandidates: `${GHALISHOR_API_V1_PREFIX}/business/dispatch/candidates`,
  dispatchRuns: `${GHALISHOR_API_V1_PREFIX}/business/dispatch/runs`,
  driverRuns: `${GHALISHOR_API_V1_PREFIX}/business/driver/runs`,
  startDriverRun: (id: string) => `${GHALISHOR_API_V1_PREFIX}/business/driver/runs/${encodeURIComponent(id)}/start`,
  planRun: `${GHALISHOR_API_V1_PREFIX}/business/dispatch/runs`,
  assignRun: (id: string) => `${GHALISHOR_API_V1_PREFIX}/business/dispatch/runs/${encodeURIComponent(id)}/assign`,
  cancelRun: (id: string) => `${GHALISHOR_API_V1_PREFIX}/business/dispatch/runs/${encodeURIComponent(id)}/cancel`,
  updateStop: (id: string) => `${GHALISHOR_API_V1_PREFIX}/business/driver/stops/${encodeURIComponent(id)}`,
} as const);

export type DriverAvailabilityStatus = 'offline' | 'available' | 'on_route' | 'paused';
export type DispatchRunStatus = 'draft' | 'planned' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
export type DispatchStopStatus = 'pending' | 'en_route' | 'arrived' | 'completed' | 'skipped';

export interface UpdateDriverLocationRequest {
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracyMeters?: number | null;
  readonly speedMetersPerSecond?: number | null;
  readonly headingDegrees?: number | null;
  readonly status: Exclude<DriverAvailabilityStatus, 'offline'>;
  readonly recordedAt: IsoDateTimeString;
}

export interface BusinessDriverLocation {
  readonly membershipId: UuidString;
  readonly userId: UuidString;
  readonly name: string | null;
  readonly mobileE164: string;
  readonly status: DriverAvailabilityStatus;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly accuracyMeters: number | null;
  readonly recordedAt: IsoDateTimeString | null;
  readonly isStale: boolean;
}

export interface DispatchStopSummary {
  readonly id: UuidString;
  readonly serviceRequestId: UuidString;
  readonly kind: 'pickup' | 'delivery';
  readonly sequence: number;
  readonly latitude: number;
  readonly longitude: number;
  readonly addressText: string | null;
  readonly customerName: string | null;
  readonly customerMobileE164: string;
  readonly scheduledAt: IsoDateTimeString | null;
  readonly status: DispatchStopStatus;
}

export interface DispatchRunSummary {
  readonly id: UuidString;
  readonly businessId: UuidString;
  readonly driverMembershipId: UuidString | null;
  readonly driverName: string | null;
  readonly serviceDate: string;
  readonly status: DispatchRunStatus;
  readonly routeProvider: 'internal' | 'neshan';
  readonly routeDistanceMeters: number | null;
  readonly routeDurationSeconds: number | null;
  readonly stops: readonly DispatchStopSummary[];
  readonly startedAt: IsoDateTimeString | null;
  readonly completedAt: IsoDateTimeString | null;
  readonly cancelledAt: IsoDateTimeString | null;
  readonly cancellationReason: string | null;
  readonly createdAt: IsoDateTimeString;
  readonly updatedAt: IsoDateTimeString;
}

export interface DispatchCandidate {
  readonly serviceRequestId: UuidString;
  readonly customerName: string | null;
  readonly customerMobileE164: string;
  readonly addressText: string | null;
  readonly latitude: number;
  readonly longitude: number;
  readonly scheduledPickupAt: IsoDateTimeString | null;
  readonly itemType: string;
  readonly itemCount: number;
  readonly status: string;
}

export interface PlanDispatchRunRequest {
  readonly serviceRequestIds: readonly UuidString[];
  readonly serviceDate: string;
  readonly driverMembershipId?: UuidString | null;
}

export interface AssignDispatchRunRequest {
  readonly driverMembershipId: UuidString;
}

export interface CancelDispatchRunRequest {
  readonly reason: string;
}

export interface UpdateDispatchStopRequest {
  readonly status: Extract<DispatchStopStatus, 'en_route' | 'arrived' | 'completed' | 'skipped'>;
}

export interface ListBusinessDriversResponse {
  readonly items: readonly BusinessDriverLocation[];
  readonly generatedAt: IsoDateTimeString;
}

export interface ListDriverRunsResponse {
  readonly items: readonly DispatchRunSummary[];
}

export interface ListBusinessDispatchRunsResponse {
  readonly items: readonly DispatchRunSummary[];
}

export interface ListDispatchCandidatesResponse {
  readonly items: readonly DispatchCandidate[];
}
